import React, { createContext, useContext, useReducer, ReactNode } from 'react'
import { getFenceConfig, getFenceConfigWithColor } from '../config/fenceConfigs'

// 🚀 DEBUG FLAG - ustaw na true tylko do debugowania
const DEBUG_LOGS = false

export interface FenceElement {
  id: string
  type: 'post' | 'gate' | 'door' | 'section'
  position: { x: number; y: number; z: number }
  rotation: number
  width: number
  height: number
  material: string
  price: number
  widthMultiplier?: number // 🎯 Szerokość przęsła (1x-5x)
  variant?: string // 🎯 Wariant elementu (np. 'p22', 'p38', 's22', 's38' dla Łupane Muro - Pełne)
  isClosingSection?: boolean // 🎯 Czy to sekcja zamykająca obwód
}

export interface FenceSystem {
  id: string
  name: string
  objFile: string
  mtlFile: string
  textureFile: string
  daeFile: string // 🎯 Dodaję właściwość daeFile
  texturePath: string // 🎨 Ścieżka do folderu z teksturami
  basePrice: number // 💰 DEPRECATED - użyj blockPrice
  blockPrice?: number // 💰 Cena pojedynczego bloczka zwykłego (zł brutto)
  decorationBlockPrice?: number // 🎨 Cena pojedynczego bloczka dekoracyjnego (zł brutto)
  blockHeight?: number // 📐 Wysokość pojedynczego bloczka (cm)
  dimensions?: string // 📐 Wymiary bloczka (np. "100×20×30")
}

export interface HouseStyle {
  id: string
  name: string
  model: string
}

export interface FenceState {
  selectedSystem: FenceSystem | null
  selectedHouseStyle: HouseStyle | null
  elements: FenceElement[]
  viewMode: '2D' | '3D'
  totalLength: number
  totalPrice: number
  isClosed: boolean
  activeTab: 'konfigurator' | 'wizualizator'
  selectedColor: string | null // 🎨 Dodaję wybór koloru
  selectedVariant: string | null // 🎯 Dodaję wybór wariantu (np. 'p22', 'p38', 's22', 's38')
}

type FenceAction =
  | { type: 'SELECT_SYSTEM'; payload: FenceSystem }
  | { type: 'SELECT_HOUSE_STYLE'; payload: HouseStyle | null }
  | { type: 'SET_VIEW_MODE'; payload: '2D' | '3D' }
  | { type: 'SET_ACTIVE_TAB'; payload: 'konfigurator' | 'wizualizator' }
  | { type: 'ADD_ELEMENT'; payload: FenceElement }
  | { type: 'UPDATE_ELEMENT'; payload: { id: string; updates: Partial<FenceElement> } }
  | { type: 'REMOVE_ELEMENT'; payload: string }
  | { type: 'ROTATE_ELEMENT'; payload: { id: string; rotation: number } }
  | { type: 'SELECT_COLOR'; payload: string | null } // 🎨 Dodaję akcję wyboru koloru
  | { type: 'SELECT_VARIANT'; payload: string | null } // 🎯 Dodaję akcję wyboru wariantu
  | { type: 'RESET_PROJECT' }
  | { type: 'CLEAR_ELEMENTS' } // 🧹 Czyści tylko elementy, bez resetu systemu
  | { type: 'CLOSE_PERIMETER' }
  | { type: 'UPDATE_TOTALS' }

const initialState: FenceState = {
  selectedSystem: null,
  selectedHouseStyle: null,
  elements: [],
  viewMode: '2D', // 🎯 Przywracam domyślny widok 2D
  totalLength: 0,
  totalPrice: 0,
  isClosed: false,
  activeTab: 'konfigurator',
  selectedColor: null, // 🎨 Domyślnie brak wybranego koloru
  selectedVariant: null // 🎯 Domyślnie brak wybranego wariantu
}

function fenceReducer(state: FenceState, action: FenceAction): FenceState {
  switch (action.type) {
    case 'SELECT_SYSTEM':
      return { ...state, selectedSystem: action.payload }
    
    case 'SELECT_HOUSE_STYLE':
      return { ...state, selectedHouseStyle: action.payload }
    
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload }
    
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload }
    
    case 'SELECT_COLOR':
      console.log('🎨 FenceReducer: SELECT_COLOR - payload:', action.payload)
      console.log('🎨 FenceReducer: SELECT_COLOR - przed zmianą selectedColor:', state.selectedColor)
      const colorState = { ...state, selectedColor: action.payload }
      console.log('🎨 FenceReducer: SELECT_COLOR - po zmianie selectedColor:', colorState.selectedColor)
      return colorState
    
    case 'SELECT_VARIANT':
      console.log('🎯 FenceReducer: SELECT_VARIANT - payload:', action.payload)
      const variantState = { ...state, selectedVariant: action.payload }
      console.log('🎯 FenceReducer: SELECT_VARIANT - po zmianie selectedVariant:', variantState.selectedVariant)
      return variantState
    
    case 'ADD_ELEMENT':
      const callId = `reducer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      DEBUG_LOGS && console.log(`🔄 FenceReducer [${callId}]: Dodaję element do state:`, action.payload)
      // 🚫 Jeśli element jest sekcją zamykającą, ustaw isClosed na true
      // 🚫 Jeśli obwód jest już zamknięty, zachowaj isClosed = true
      // 🚫 W przeciwnym razie resetuj isClosed na false
      const newIsClosed = action.payload.isClosingSection ? true : (state.isClosed ? true : false)
      const elementState = { 
        ...state, 
        elements: [...state.elements, action.payload],
        isClosed: newIsClosed
      }
      // Automatycznie aktualizuj totala (wyklucz door i gate z ceny)
      const newTotalLength = elementState.elements.reduce((sum, el) => sum + el.width, 0)
      const newTotalPrice = elementState.elements
        .filter(el => el.type !== 'door' && el.type !== 'gate')
        .reduce((sum, el) => sum + el.price, 0)
      DEBUG_LOGS && console.log(`🔄 FenceReducer [${callId}]: Nowa liczba elementów: ${elementState.elements.length}, totalLength: ${newTotalLength}, totalPrice: ${newTotalPrice}`)
      return { 
        ...elementState, 
        totalLength: newTotalLength, 
        totalPrice: newTotalPrice 
      }
    
    case 'UPDATE_ELEMENT':
      DEBUG_LOGS && console.log(`🎯 Reducer UPDATE_ELEMENT: ${action.payload.id}`, action.payload.updates)
      const updatedState = {
        ...state,
        elements: state.elements.map(el =>
          el.id === action.payload.id ? { ...el, ...action.payload.updates } : el
        )
      }
      DEBUG_LOGS && console.log(`🎯 Reducer: Element po aktualizacji:`, updatedState.elements.find(el => el.id === action.payload.id))
      // Automatycznie aktualizuj totala (wyklucz door i gate z ceny)
      const updatedTotalLength = updatedState.elements.reduce((sum, el) => sum + el.width, 0)
      const updatedTotalPrice = updatedState.elements
        .filter(el => el.type !== 'door' && el.type !== 'gate')
        .reduce((sum, el) => sum + el.price, 0)
      return { 
        ...updatedState, 
        totalLength: updatedTotalLength, 
        totalPrice: updatedTotalPrice 
      }
    
    case 'REMOVE_ELEMENT':
      const removedState = {
        ...state,
        elements: state.elements.filter(el => el.id !== action.payload),
        isClosed: false
      }
      // Automatycznie aktualizuj totala (wyklucz door i gate z ceny)
      const removedTotalLength = removedState.elements.reduce((sum, el) => sum + el.width, 0)
      const removedTotalPrice = removedState.elements
        .filter(el => el.type !== 'door' && el.type !== 'gate')
        .reduce((sum, el) => sum + el.price, 0)
      return { 
        ...removedState, 
        totalLength: removedTotalLength, 
        totalPrice: removedTotalPrice 
      }
    
    case 'ROTATE_ELEMENT':
      return {
        ...state,
        elements: state.elements.map(el =>
          el.id === action.payload.id ? { ...el, rotation: action.payload.rotation } : el
        )
      }
    
    case 'RESET_PROJECT':
      console.log('🔄 FenceReducer: Przetwarzam RESET_PROJECT')
      console.log('🔍 FenceReducer: Stan przed resetem:', state.elements.length, 'elementów')
      const resetState = {
        ...state,
        elements: [],
        totalLength: 0,
        totalPrice: 0,
        isClosed: false
      }
      console.log('✅ FenceReducer: Stan po resecie:', resetState.elements.length, 'elementów')
      return resetState
    
    case 'CLEAR_ELEMENTS':
      console.log('🧹 FenceReducer: Czyszczenie wszystkich elementów (zachowuję system)')
      console.log('🔍 FenceReducer: Stan przed czyszczeniem:', state.elements.length, 'elementów')
      const clearState = {
        ...state,
        elements: [],
        totalLength: 0,
        totalPrice: 0,
        isClosed: false
      }
      console.log('✅ FenceReducer: Stan po czyszczeniu:', clearState.elements.length, 'elementów')
      return clearState
    
    case 'CLOSE_PERIMETER':
      return { ...state, isClosed: true }
    
    case 'UPDATE_TOTALS':
      const totalLength = state.elements.reduce((sum, el) => sum + el.width, 0)
      const totalPrice = state.elements
        .filter(el => el.type !== 'door' && el.type !== 'gate')
        .reduce((sum, el) => sum + el.price, 0)
      return { ...state, totalLength, totalPrice }
    
    default:
      return state
  }
}

interface FenceContextType {
  state: FenceState
  dispatch: React.Dispatch<FenceAction>
  addPost: (position: { x: number; y: number; z: number }, dimensions?: { width: number; height: number }, variant?: string) => Promise<string>
  addGate: (position: { x: number; y: number; z: number }, dimensions?: { width: number; height: number }, variant?: string) => Promise<string>
  addDoor: (position: { x: number; y: number; z: number }, dimensions?: { width: number; height: number }, variant?: string) => Promise<string>
  addSection: (position: { x: number; y: number; z: number }, width?: number, height?: number, variant?: string, widthMultiplier?: number, isClosingSection?: boolean) => Promise<string>
  removeElement: (id: string) => void
  updateElement: (id: string, updates: Partial<FenceElement>) => void
  updateSectionWidth: (sectionId: string, newWidthMultiplier: number) => Promise<void> // 🎯 Aktualizuj szerokość przęsła
  updateGateWidth: (gateId: string, newWidthMultiplier: number) => Promise<void> // 🚪 Aktualizuj szerokość bramy
  updateDoorWidth: (doorId: string, newWidthMultiplier: number) => Promise<void> // 🚪 Aktualizuj szerokość drzwi
  rotateElement: (id: string, rotation: number) => void
  resetProject: () => void
  closePerimeter: () => void
  updateTotals: () => void
  selectColor: (color: string | null) => void // 🎨 Dodaję funkcję wyboru koloru
  selectVariant: (variant: string | null) => void // 🎯 Dodaję funkcję wyboru wariantu
  recalculateAllPrices: (variant?: string) => Promise<void> // 💰 Przelicz ceny wszystkich elementów na podstawie wariantu
  onReset?: () => void // Callback dla resetowania sceny
}

const FenceContext = createContext<FenceContextType | undefined>(undefined)

export function FenceProvider({ children, onReset }: { children: ReactNode, onReset?: () => void }) {
  const [state, dispatch] = useReducer(fenceReducer, initialState)

  const addPost = async (position: { x: number; y: number; z: number }, dimensions?: { width: number; height: number }, variant?: string) => {
    const callId = `addPost-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    DEBUG_LOGS && console.log(`📝 FenceContext.addPost [${callId}]: Wywołuję funkcję - position:`, position, `dimensions:`, dimensions)
    
    // 🎯 NOWE: Nie dziedzicz rotacji w React state - zawsze suwak na 0
    // Wizualna orientacja jest ustawiana w 3D przez syncElementRotation
    DEBUG_LOGS && console.log(`🎯 Nowy element będzie miał suwak na 0° (środek) w React state`)
    DEBUG_LOGS && console.log(`💡 Orientacja wizualna będzie ustawiona przez syncElementRotation w 3D`)
    
    // 🎯 OBLICZ SZEROKOŚĆ NA PODSTAWIE WYBRANEGO SYSTEMU
    let postWidth = dimensions?.width || 0.8 // Domyślna szerokość
    DEBUG_LOGS && console.log(`🔍 FenceContext: dimensions?.width = ${dimensions?.width}, default = 0.8, using: ${postWidth}`)
    
    if (state.selectedSystem) {
      const fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      if (fenceConfig?.elements?.post?.spacing) {
        // Użyj 2x spacing z konfiguracji (jak w ThreeJSCanvas3D)
        const configWidth = fenceConfig.elements.post.spacing * 2
        DEBUG_LOGS && console.log(`🎯 Config width from ${state.selectedSystem.id}: ${configWidth} (spacing: ${fenceConfig.elements.post.spacing})`)
        
        // 🎯 KLUCZOWE: Jeśli dimensions są przekazane z 3D, użyj ich zamiast config
        if (dimensions?.width) {
          postWidth = dimensions.width
          DEBUG_LOGS && console.log(`✅ Using dimensions from 3D scene: ${postWidth}`)
        } else {
          postWidth = configWidth
          DEBUG_LOGS && console.log(`✅ Using dimensions from config: ${postWidth}`)
        }
      }
    }
    
    DEBUG_LOGS && console.log(`📏 FINAL POST WIDTH: ${postWidth}m (from ${dimensions?.width ? '3D scene' : 'config/default'})`)

    // 🎯 WYSOKOŚĆ SŁUPKA (w BLOKACH):
    // - pochodzi z UI / 3D (dimensions.height = ilość bloków, np. 12 dla Slim),
    // - NIE nadpisujemy jej defaultHeight z hardcoded configu (który dla Slim ma 4),
    //   żeby suwak zawsze pokazywał to, co użytkownik wybrał (np. 11 + 1 dekoracyjny).
    let postBlocks = dimensions?.height || 6 // całkowita liczba bloków (zwykłe + dekoracyjne)

    // 💰 Oblicz cenę słupka na podstawie konfiguracji
    let postPrice = 50 // domyślna
    if (state.selectedSystem) {
      // 🎯 Dla Royal Mix / Muro Mix: Załaduj konfigurację wariantu jeśli jest dostępny
      let fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      
      if (variant && fenceConfig?.variantConfigs?.[variant]) {
        const variantConfigId = fenceConfig.variantConfigs[variant]
        const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
        if (variantConfig) {
          fenceConfig = variantConfig
          DEBUG_LOGS && console.log(`💰 addPost: Używam konfiguracji wariantu ${variant} -> ${variantConfigId} dla obliczenia ceny`)
        }
      }
      
      // 🎨 Sprawdź czy są ceny zależne od koloru (priorytet ma wariant)
      let blockPrice = (variant ? fenceConfig?.blockPrice : state.selectedSystem.blockPrice) || fenceConfig?.blockPrice || 150
      let decorationBlockPrice = (variant ? fenceConfig?.decorationBlockPrice : state.selectedSystem.decorationBlockPrice) || fenceConfig?.decorationBlockPrice || blockPrice
      
      if (state.selectedColor && fenceConfig?.colorPrices?.[state.selectedColor]) {
        const colorPricing = fenceConfig.colorPrices[state.selectedColor]
        if (colorPricing.blockPrice) blockPrice = colorPricing.blockPrice
        if (colorPricing.decorationBlockPrice) decorationBlockPrice = colorPricing.decorationBlockPrice
        DEBUG_LOGS && console.log(`🎨 Używam cen dla koloru "${state.selectedColor}": bloczek=${blockPrice}, dekoracyjny=${decorationBlockPrice}`)
      }

      // 🎯 UŻYJ LICZBY BLOKÓW Z postBlocks, A NIE defaultHeight z hardcoded configu
      const decorationBlocksFromConfig = fenceConfig?.elements?.post?.decorationBlocksCount ?? 1
      const regularBlocksFromConfig = Math.max(postBlocks - decorationBlocksFromConfig, 0)

      DEBUG_LOGS && console.log(`🔍 DEBUG: postBlocks (z UI/3D) = ${postBlocks}, regular=${regularBlocksFromConfig}, decorative=${decorationBlocksFromConfig}`)
      
      postPrice = (regularBlocksFromConfig * blockPrice) + (decorationBlocksFromConfig * decorationBlockPrice)
      DEBUG_LOGS && console.log(`💰 Obliczona cena słupka: (${regularBlocksFromConfig} × ${blockPrice}) + (${decorationBlocksFromConfig} × ${decorationBlockPrice}) = ${postPrice.toFixed(2)} zł`)
    }
    
    const element: FenceElement = {
      id: `post-${Date.now()}`,
      type: 'post',
      position,
      width: postWidth,
      height: postBlocks, // Użyj postBlocks (z dimensions.height lub config)
      rotation: 0, // Suwak na środku (0°) - pełna kontrola rotacji w obie strony
      material: state.selectedSystem?.id || 'default',
      price: postPrice,
      variant: variant // 🎯 Zapisz wariant elementu
    }
    
    DEBUG_LOGS && console.log(`📝 FenceContext.addPost [${callId}]: Tworzę element z ID: ${element.id}, width: ${element.width}, height: ${element.height}`)
    dispatch({ type: 'ADD_ELEMENT', payload: element })
    DEBUG_LOGS && console.log(`📝 FenceContext.addPost [${callId}]: Element dodany do state z ID: ${element.id}`)
    
    return element.id
  }

  const addGate = async (position: { x: number; y: number; z: number }, dimensions?: { width: number; height: number }, variant?: string): Promise<string> => {
    // 🎯 NOWE: Nie dziedzicz rotacji w React state - zawsze suwak na 0
    DEBUG_LOGS && console.log(`🎯 Nowa brama będzie miała suwak na 0° (środek) w React state`)
    
    // 💰 Oblicz cenę bramy na podstawie konfiguracji
    let gatePrice = 1200 // domyślna
    const defaultWidthMultiplier = 2 // 🚪 Brama domyślnie 2x
    
    if (state.selectedSystem) {
      // 🎯 Dla Royal Mix / Muro Mix: Załaduj konfigurację wariantu jeśli jest dostępny
      let fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      
      if (variant && fenceConfig?.variantConfigs?.[variant]) {
        const variantConfigId = fenceConfig.variantConfigs[variant]
        const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
        if (variantConfig) {
          fenceConfig = variantConfig
          DEBUG_LOGS && console.log(`💰 addGate: Używam konfiguracji wariantu ${variant} -> ${variantConfigId} dla obliczenia ceny`)
        }
      }
      
      const baseGatePrice = fenceConfig?.elements?.gate?.basePrice ?? 1000
      gatePrice = baseGatePrice * defaultWidthMultiplier
      DEBUG_LOGS && console.log(`💰 Obliczona cena bramy: ${baseGatePrice} × ${defaultWidthMultiplier} = ${gatePrice.toFixed(2)} zł`)
    }
    
    const element: FenceElement = {
      id: `gate-${Date.now()}`,
      type: 'gate',
      position,
      rotation: 0, // Suwak na 0° (środek)
      width: dimensions?.width || 3.0,
      height: dimensions?.height || 2.0,
      material: state.selectedSystem?.id || 'default',
      price: gatePrice,
      widthMultiplier: defaultWidthMultiplier,
      variant: variant // 🎯 Zapisz wariant elementu
    }
    dispatch({ type: 'ADD_ELEMENT', payload: element })
    return element.id
  }

  const addDoor = async (position: { x: number; y: number; z: number }, dimensions?: { width: number; height: number }, variant?: string): Promise<string> => {
    // 🎯 NOWE: Nie dziedzicz rotacji w React state - zawsze suwak na 0
    DEBUG_LOGS && console.log(`🎯 Nowe drzwi będą miały suwak na 0° (środek) w React state`)
    
    // 💰 Oblicz cenę drzwi na podstawie konfiguracji
    let doorPrice = 600 // domyślna
    // 🎯 Dla Royal S50, Royal P50: domyślnie 2x szerokość dla drzwi
    let defaultWidthMultiplier = 1 // 🚪 Drzwi domyślnie 1x
    
    if (state.selectedSystem) {
      // 🎯 Dla Royal Mix / Muro Mix: Załaduj konfigurację wariantu jeśli jest dostępny
      let fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      
      if (variant && fenceConfig?.variantConfigs?.[variant]) {
        const variantConfigId = fenceConfig.variantConfigs[variant]
        const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
        if (variantConfig) {
          fenceConfig = variantConfig
          DEBUG_LOGS && console.log(`💰 addDoor: Używam konfiguracji wariantu ${variant} -> ${variantConfigId} dla obliczenia ceny`)
        }
      }
      
      // 🎯 Sprawdź czy to Royal S50 lub P50 (lub wariant Royal Mix, lub Royal Mix bez wariantu)
      const isRoyalMix = state.selectedSystem.id === 'royal-mix' || fenceConfig?.id === 'royal-mix'
      if (fenceConfig?.id === 'royal_s50' || fenceConfig?.id === 'royal_p50' || variant === 's50' || variant === 'p50' || isRoyalMix) {
        defaultWidthMultiplier = 2 // Dla Royal S50/P50 i Royal Mix domyślnie 2x
        DEBUG_LOGS && console.log(`🎯 addDoor: Ustawiono defaultWidthMultiplier=2 dla Royal Mix/S50/P50`)
      }
      const baseDoorPrice = fenceConfig?.elements?.door?.basePrice ?? 525
      doorPrice = baseDoorPrice * defaultWidthMultiplier
      DEBUG_LOGS && console.log(`💰 Obliczona cena drzwi: ${baseDoorPrice} × ${defaultWidthMultiplier} = ${doorPrice.toFixed(2)} zł`)
    }
    
    const element: FenceElement = {
      id: `door-${Date.now()}`,
      type: 'door',
      position,
      rotation: 0, // Suwak na 0° (środek)
      width: dimensions?.width || 1.0,
      height: dimensions?.height || 2.0,
      material: state.selectedSystem?.id || 'default',
      price: doorPrice,
      widthMultiplier: defaultWidthMultiplier,
      variant: variant // 🎯 Zapisz wariant elementu
    }
    dispatch({ type: 'ADD_ELEMENT', payload: element })
    return element.id
  }

  const addSection = async (position: { x: number; y: number; z: number }, width?: number, height?: number, variant?: string, widthMultiplier?: number, isClosingSection?: boolean) => {
    const callId = `addSection-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    DEBUG_LOGS && console.log(`📝 FenceContext.addSection [${callId}]: Wywołuję funkcję - position:`, position, `width:`, width, `height:`, height, `widthMultiplier:`, widthMultiplier)
    
    // 🎯 NOWE: Nie dziedzicz rotacji w React state - zawsze suwak na 0
    DEBUG_LOGS && console.log(`🎯 Nowe przęsło będzie miało suwak na 0° (środek) w React state`)
    
    // 💰 Oblicz cenę przęsła na podstawie konfiguracji
    let sectionPrice = 100 // domyślna
    let defaultWidthMultiplier = 1
    let initialHeight = height || 2 // 🎯 Domyślnie 2 (1 zwykły + 1 dekoracyjny)
    
    if (state.selectedSystem) {
      // 🎯 Dla Royal Mix / Muro Mix: Załaduj konfigurację wariantu jeśli jest dostępny
      let fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      
      if (variant && fenceConfig?.variantConfigs?.[variant]) {
        const variantConfigId = fenceConfig.variantConfigs[variant]
        const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
        if (variantConfig) {
          fenceConfig = variantConfig
          DEBUG_LOGS && console.log(`💰 addSection: Używam konfiguracji wariantu ${variant} -> ${variantConfigId} dla obliczenia ceny`)
        }
      }
      
      // 🎨 Sprawdź czy są ceny zależne od koloru
      let blockPrice = state.selectedSystem.blockPrice || fenceConfig?.blockPrice || 150
      let decorationBlockPrice = state.selectedSystem.decorationBlockPrice || fenceConfig?.decorationBlockPrice || blockPrice
      
      if (state.selectedColor && fenceConfig?.colorPrices?.[state.selectedColor]) {
        const colorPricing = fenceConfig.colorPrices[state.selectedColor]
        if (colorPricing.blockPrice) blockPrice = colorPricing.blockPrice
        if (colorPricing.decorationBlockPrice) decorationBlockPrice = colorPricing.decorationBlockPrice
        DEBUG_LOGS && console.log(`🎨 Używam cen dla koloru "${state.selectedColor}": bloczek=${blockPrice}, dekoracyjny=${decorationBlockPrice}`)
      }
      
      // 🎯 Dla Muro P22, P38, S22, S38: domyślnie 3x szerokość (3 zestawienia obok siebie)
      // 🎯 Dla Royal S50, Royal P50, Royal Mix: domyślnie 2x szerokość (2 zestawienia obok siebie)
      // 🎯 Dla "Pełne" (lupane-muro-full) i "Mix" (lupane-muro-mix): wszystkie warianty mają 3x szerokość
      if (fenceConfig?.id === 'lupane-muro-p22' || fenceConfig?.id === 'lupane-muro-p38' || 
          fenceConfig?.id === 'lupane-muro-s22' || fenceConfig?.id === 'lupane-muro-s38' ||
          fenceConfig?.id === 'lupane-muro-full' || fenceConfig?.id === 'lupane-muro-mix') {
        defaultWidthMultiplier = 3
      } else if (fenceConfig?.id === 'royal_s50' || fenceConfig?.id === 'royal_p50' || fenceConfig?.id === 'royal-mix') {
        defaultWidthMultiplier = 2
      }
      
      // 🎯 Użyj przekazanego widthMultiplier jeśli jest dostępny (dla sekcji zamykającej)
      const finalWidthMultiplier = widthMultiplier !== undefined ? widthMultiplier : defaultWidthMultiplier
      
      // Dla przęseł: użyj heightBlocks z elementu (lub defaultHeight z konfiguracji)
      const decorationBlocksCount = fenceConfig?.elements?.section?.decorationBlocksCount ?? 1
      // 🎯 Jeśli nie podano height, początkowy element ma już 1 zwykły + 1 dekoracyjny z modelu
      // więc początkowa wysokość = 1 (zwykły) + decorationBlocksCount (dekoracyjny)
      initialHeight = height || (decorationBlocksCount + 1) // 1 zwykły + dekoracyjne
      const sectionHeightBlocks = initialHeight || fenceConfig?.elements?.section?.defaultHeight || 1
      const decorative = Math.min(decorationBlocksCount, sectionHeightBlocks)
      const regular = Math.max(sectionHeightBlocks - decorative, 0)
      
      // Oblicz cenę: (bloki zwykłe + bloki dekoracyjne) × widthMultiplier
      // 🚫 Nie liczymy metalPrice - tylko bloki
      const basePrice = (regular * blockPrice) + (decorative * decorationBlockPrice)
      sectionPrice = basePrice * finalWidthMultiplier
      
      console.log(`💰 FenceContext.addSection: Obliczona cena przęsła:`, {
        regular,
        decorative,
        blockPrice,
        decorationBlockPrice,
        basePrice: basePrice.toFixed(2),
        finalWidthMultiplier,
        sectionPrice: sectionPrice.toFixed(2),
        width: width,
        height: initialHeight
      })
      DEBUG_LOGS && console.log(`💰 Obliczona cena przęsła: ((${regular} × ${blockPrice}) + (${decorative} × ${decorationBlockPrice})) × ${finalWidthMultiplier} = ${sectionPrice.toFixed(2)} zł`)
    }
    
    const element: FenceElement = {
      id: `section-${Date.now()}`,
      type: 'section',
      position,
      width: width || 2.0,
      // 🎯 Jeśli nie podano height, użyj początkową wysokość uwzględniającą dekorację (1 zwykły + dekoracyjne)
      height: initialHeight,
      rotation: 0, // Suwak na 0° (środek)
      material: state.selectedSystem?.id || 'default',
      price: sectionPrice,
      widthMultiplier: widthMultiplier !== undefined ? widthMultiplier : defaultWidthMultiplier, // 🎯 Użyj przekazanego widthMultiplier lub defaultWidthMultiplier
      variant: variant, // 🎯 Zapisz wariant elementu
      isClosingSection: isClosingSection || false // 🎯 Zapisz czy to sekcja zamykająca
    }
    
    DEBUG_LOGS && console.log(`📝 FenceContext.addSection [${callId}]: Tworzę element z ID: ${element.id}`)
    dispatch({ type: 'ADD_ELEMENT', payload: element })
    DEBUG_LOGS && console.log(`📝 FenceContext.addSection [${callId}]: Element dodany do state z ID: ${element.id}`)
    
    return element.id
  }

  const removeElement = (id: string) => {
    dispatch({ type: 'REMOVE_ELEMENT', payload: id })
  }

  const updateElement = (id: string, updates: Partial<FenceElement>) => {
    dispatch({ type: 'UPDATE_ELEMENT', payload: { id, updates } })
  }

  // 🎯 Aktualizuj szerokość przęsła (wymaga odświeżenia 3D)
  const updateSectionWidth = async (sectionId: string, newWidthMultiplier: number) => {
    DEBUG_LOGS && console.log(`🎯 updateSectionWidth: ${sectionId} → widthMultiplier=${newWidthMultiplier}`)
    DEBUG_LOGS && console.log(`🎯 Wszystkie elementy w state:`, state.elements.map(el => ({ id: el.id, type: el.type, widthMultiplier: el.widthMultiplier })))
    
    // Znajdź element w state
    const section = state.elements.find(el => el.id === sectionId)
    if (!section || section.type !== 'section') {
      console.error(`❌ Element ${sectionId} nie jest przęsłem. Znaleziony element:`, section)
      return
    }
    
    DEBUG_LOGS && console.log(`🎯 Znaleziony section:`, section)
    
    // 💰 Przelicz cenę na podstawie nowego widthMultiplier
    let newPrice = section.price // Domyślnie zostaw starą cenę
    if (state.selectedSystem) {
      const fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      
      // 🎨 Sprawdź czy są ceny zależne od koloru
      let blockPrice = state.selectedSystem.blockPrice || fenceConfig?.blockPrice || 150
      let decorationBlockPrice = state.selectedSystem.decorationBlockPrice || fenceConfig?.decorationBlockPrice || blockPrice
      
      if (state.selectedColor && fenceConfig?.colorPrices?.[state.selectedColor]) {
        const colorPricing = fenceConfig.colorPrices[state.selectedColor]
        if (colorPricing.blockPrice) blockPrice = colorPricing.blockPrice
        if (colorPricing.decorationBlockPrice) decorationBlockPrice = colorPricing.decorationBlockPrice
      }
      
      // Dla przęseł: użyj heightBlocks z elementu (lub defaultHeight z konfiguracji)
      const sectionHeightBlocks = section.height || fenceConfig?.elements?.section?.defaultHeight || 1
      const decorationBlocksCount = fenceConfig?.elements?.section?.decorationBlocksCount ?? 1
      const decorative = Math.min(decorationBlocksCount, sectionHeightBlocks)
      const regular = Math.max(sectionHeightBlocks - decorative, 0)
      // Oblicz cenę: (bloki zwykłe + bloki dekoracyjne) × widthMultiplier
      // 🚫 Nie liczymy metalPrice - tylko bloki
      const basePrice = (regular * blockPrice) + (decorative * decorationBlockPrice)
      newPrice = basePrice * newWidthMultiplier
      
      DEBUG_LOGS && console.log(`💰 Przeliczona cena przęsła ${sectionId}: ((${regular} × ${blockPrice}) + (${decorative} × ${decorationBlockPrice})) × ${newWidthMultiplier} = ${newPrice.toFixed(2)} zł`)
    }
    
    // Aktualizuj widthMultiplier i cenę w React state
    dispatch({ 
      type: 'UPDATE_ELEMENT', 
      payload: { 
        id: sectionId, 
        updates: { 
          widthMultiplier: newWidthMultiplier,
          price: newPrice
        } 
      } 
    })
    
    DEBUG_LOGS && console.log(`🎯 Dispatch wykonany, sprawdzam czy 3D funkcja istnieje...`)
    
    // Wywołaj funkcję 3D do odświeżenia przęsła
    if ((window as any).updateSectionWidth) {
      DEBUG_LOGS && console.log(`🎯 Wywołuję 3D updateSectionWidth...`)
      await (window as any).updateSectionWidth(sectionId, newWidthMultiplier)
      DEBUG_LOGS && console.log(`✅ Przęsło ${sectionId} odświeżone w 3D`)
    } else {
      console.warn(`⚠️ updateSectionWidth nie jest dostępna w 3D`)
      DEBUG_LOGS && console.log(`🎯 Dostępne funkcje w window:`, Object.keys(window).filter(key => key.includes('update') || key.includes('Section')))
    }
  }

  const updateGateWidth = async (gateId: string, newWidthMultiplier: number) => {
    DEBUG_LOGS && console.log(`🚪 updateGateWidth: ${gateId} → widthMultiplier=${newWidthMultiplier}`)
    
    const gate = state.elements.find(el => el.id === gateId)
    if (!gate || gate.type !== 'gate') {
      console.error(`❌ Element ${gateId} nie jest bramą. Znaleziony element:`, gate)
      return
    }
    
    DEBUG_LOGS && console.log(`🎯 Znaleziona brama:`, gate)
    
    // 💰 Przelicz cenę na podstawie konfiguracji
    let newPrice = gate.price // Domyślnie zostaw starą cenę
    if (state.selectedSystem) {
      const fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      const baseGatePrice = fenceConfig?.elements?.gate?.basePrice ?? 1000
      newPrice = baseGatePrice * newWidthMultiplier
      DEBUG_LOGS && console.log(`💰 Przeliczona cena bramy ${gateId}: ${baseGatePrice} × ${newWidthMultiplier} = ${newPrice.toFixed(2)} zł`)
    }
    
    // Aktualizuj widthMultiplier i cenę w React state
    dispatch({ 
      type: 'UPDATE_ELEMENT', 
      payload: { 
        id: gateId, 
        updates: { 
          widthMultiplier: newWidthMultiplier,
          price: newPrice
        } 
      } 
    })
    
    // Wywołaj funkcję 3D
    if ((window as any).updateSectionWidth) {
      await (window as any).updateSectionWidth(gateId, newWidthMultiplier)
      DEBUG_LOGS && console.log(`✅ Brama ${gateId} odświeżona w 3D`)
    }
  }

  const updateDoorWidth = async (doorId: string, newWidthMultiplier: number) => {
    DEBUG_LOGS && console.log(`🚪 updateDoorWidth: ${doorId} → widthMultiplier=${newWidthMultiplier}`)
    
    const door = state.elements.find(el => el.id === doorId)
    if (!door || door.type !== 'door') {
      console.error(`❌ Element ${doorId} nie jest drzwiami. Znaleziony element:`, door)
      return
    }
    
    DEBUG_LOGS && console.log(`🎯 Znalezione drzwi:`, door)
    
    // 💰 Przelicz cenę na podstawie konfiguracji
    let newPrice = door.price // Domyślnie zostaw starą cenę
    if (state.selectedSystem) {
      const fenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
      const baseDoorPrice = fenceConfig?.elements?.door?.basePrice ?? 525
      newPrice = baseDoorPrice * newWidthMultiplier
      DEBUG_LOGS && console.log(`💰 Przeliczona cena drzwi ${doorId}: ${baseDoorPrice} × ${newWidthMultiplier} = ${newPrice.toFixed(2)} zł`)
    }
    
    // Aktualizuj widthMultiplier i cenę w React state
    dispatch({ 
      type: 'UPDATE_ELEMENT', 
      payload: { 
        id: doorId, 
        updates: { 
          widthMultiplier: newWidthMultiplier,
          price: newPrice
        } 
      } 
    })
    
    // Wywołaj funkcję 3D
    if ((window as any).updateSectionWidth) {
      await (window as any).updateSectionWidth(doorId, newWidthMultiplier)
      DEBUG_LOGS && console.log(`✅ Drzwi ${doorId} odświeżone w 3D`)
    }
  }

  const rotateElement = (id: string, rotation: number) => {
    DEBUG_LOGS && console.log(`🔄 FenceContext.rotateElement: START - id: ${id}, rotation: ${rotation}° (suwak)`)
    dispatch({ type: 'ROTATE_ELEMENT', payload: { id, rotation } })
    
    // 🎯 KLUCZOWE: Wywołaj updateElementRotation (nie syncElementRotation!)
    // updateElementRotation = obraca wokół lewej krawędzi, przesuwając element
    // syncElementRotation = ustawia orientację BEZ przesuwania (tylko dla nowo dodanych)
    DEBUG_LOGS && console.log(`🔄 FenceContext.rotateElement: Wywołuję updateElementRotation (ręczna rotacja suwakiem)`)
    if ((window as any).updateElementRotation) {
      DEBUG_LOGS && console.log(`🔄 FenceContext.rotateElement: Wywołuję updateElementRotation(${id}, ${rotation})`)
      ;(window as any).updateElementRotation(id, rotation)
      DEBUG_LOGS && console.log(`🔄 FenceContext.rotateElement: updateElementRotation wywołana`)
    } else {
      console.warn(`⚠️ FenceContext.rotateElement: updateElementRotation nie jest dostępna`)
    }
  }

  const resetProject = () => {
    console.log('🔄 FenceContext: resetProject wywołane')
    console.log('🔍 FenceContext: Stan przed resetem:', state.elements.length, 'elementów')
    
    dispatch({ type: 'RESET_PROJECT' })
    console.log('✅ FenceContext: RESET_PROJECT dispatch wysłany')
    
    // Wywołaj callback jeśli istnieje
    if (onReset) {
      console.log('🔄 FenceContext: Wywołuję onReset callback')
      onReset()
    } else {
      console.log('⚠️ FenceContext: Brak onReset callback')
    }
    
    console.log('✅ FenceContext: resetProject zakończony')
  }

  const closePerimeter = () => {
    dispatch({ type: 'CLOSE_PERIMETER' })
  }

  const updateTotals = () => {
    dispatch({ type: 'UPDATE_TOTALS' })
  }

  // 💰 Przelicz ceny wszystkich elementów - każdy element używa swojego własnego wariantu
  const recalculateAllPrices = async (_variant?: string) => {
    if (!state.selectedSystem) return
    
    DEBUG_LOGS && console.log(`💰 recalculateAllPrices: przeliczam ceny dla wszystkich elementów (każdy używa swojego wariantu)`)
    
    // Pobierz główną konfigurację (dla elementów bez wariantu)
    const mainFenceConfig = await getFenceConfigWithColor(state.selectedSystem.id, state.selectedColor || undefined)
    
    if (!mainFenceConfig) {
      console.warn(`⚠️ Nie można załadować głównej konfiguracji`)
      return
    }
    
    // Przelicz ceny dla wszystkich elementów - każdy używa swojego własnego wariantu
    for (const element of state.elements) {
      // 🎯 Dla każdego elementu: użyj jego własnego wariantu (jeśli ma)
      let fenceConfig = mainFenceConfig
      
      // Jeśli element ma wariant, użyj konfiguracji tego wariantu
      if (element.variant && mainFenceConfig?.variantConfigs?.[element.variant]) {
        const variantConfigId = mainFenceConfig.variantConfigs[element.variant]
        const variantConfig = await getFenceConfigWithColor(variantConfigId, state.selectedColor || undefined)
        if (variantConfig) {
          fenceConfig = variantConfig
          DEBUG_LOGS && console.log(`💰 Element ${element.id} (${element.type}): używam konfiguracji wariantu ${element.variant} -> ${variantConfigId}`)
        } else {
          console.warn(`⚠️ Nie można załadować konfiguracji wariantu ${element.variant} dla elementu ${element.id}, używam głównej konfiguracji`)
        }
      } else if (element.variant) {
        DEBUG_LOGS && console.log(`💰 Element ${element.id} (${element.type}): ma wariant ${element.variant}, ale brak mapowania w variantConfigs, używam głównej konfiguracji`)
      }
      
      // 🎨 Sprawdź czy są ceny zależne od koloru (priorytet ma wariant)
      let blockPrice = (element.variant ? fenceConfig?.blockPrice : state.selectedSystem.blockPrice) || fenceConfig?.blockPrice || 150
      let decorationBlockPrice = (element.variant ? fenceConfig?.decorationBlockPrice : state.selectedSystem.decorationBlockPrice) || fenceConfig?.decorationBlockPrice || blockPrice
      
      if (state.selectedColor && fenceConfig?.colorPrices?.[state.selectedColor]) {
        const colorPricing = fenceConfig.colorPrices[state.selectedColor]
        if (colorPricing.blockPrice) blockPrice = colorPricing.blockPrice
        if (colorPricing.decorationBlockPrice) decorationBlockPrice = colorPricing.decorationBlockPrice
      }
      
      let newPrice = element.price // Domyślnie zostaw starą cenę
      
      if (element.type === 'post') {
        const postHeightBlocks = element.height || fenceConfig?.elements?.post?.defaultHeight || 6
        const decorationBlocksCount = fenceConfig?.elements?.post?.decorationBlocksCount ?? 1
        const decorative = Math.min(decorationBlocksCount, postHeightBlocks)
        const regular = Math.max(postHeightBlocks - decorative, 0)
        newPrice = (regular * blockPrice) + (decorative * decorationBlockPrice)
        DEBUG_LOGS && console.log(`💰 Przeliczona cena słupka ${element.id} (wariant: ${element.variant || 'brak'}): ((${regular} × ${blockPrice}) + (${decorative} × ${decorationBlockPrice})) = ${newPrice.toFixed(2)} zł`)
      } else if (element.type === 'section') {
        const sectionHeightBlocks = element.height || fenceConfig?.elements?.section?.defaultHeight || 1
        const decorationBlocksCount = fenceConfig?.elements?.section?.decorationBlocksCount ?? 1
        const decorative = Math.min(decorationBlocksCount, sectionHeightBlocks)
        const regular = Math.max(sectionHeightBlocks - decorative, 0)
        const widthMultiplier = element.widthMultiplier || 1
        const basePrice = (regular * blockPrice) + (decorative * decorationBlockPrice)
        newPrice = basePrice * widthMultiplier
        DEBUG_LOGS && console.log(`💰 Przeliczona cena sekcji ${element.id} (wariant: ${element.variant || 'brak'}): ((${regular} × ${blockPrice}) + (${decorative} × ${decorationBlockPrice})) × ${widthMultiplier} = ${newPrice.toFixed(2)} zł`)
      } else if (element.type === 'gate') {
        const baseGatePrice = fenceConfig?.elements?.gate?.basePrice ?? 1000
        const widthMultiplier = element.widthMultiplier || 2
        newPrice = baseGatePrice * widthMultiplier
        DEBUG_LOGS && console.log(`💰 Przeliczona cena bramy ${element.id}: ${baseGatePrice} × ${widthMultiplier} = ${newPrice.toFixed(2)} zł`)
      } else if (element.type === 'door') {
        const baseDoorPrice = fenceConfig?.elements?.door?.basePrice ?? 500
        const widthMultiplier = element.widthMultiplier || 1
        newPrice = baseDoorPrice * widthMultiplier
        DEBUG_LOGS && console.log(`💰 Przeliczona cena furtki ${element.id}: ${baseDoorPrice} × ${widthMultiplier} = ${newPrice.toFixed(2)} zł`)
      }
      
      // Aktualizuj cenę elementu
      if (Math.abs((element.price || 0) - newPrice) > 0.01) {
        dispatch({
          type: 'UPDATE_ELEMENT',
          payload: {
            id: element.id,
            updates: { price: newPrice }
          }
        })
      }
    }
    
    DEBUG_LOGS && console.log(`✅ Przeliczono ceny wszystkich elementów (każdy używał swojego wariantu)`)
  }

  const selectColor = (color: string | null) => {
    console.log('🎨 FenceContext: selectColor wywołane z color:', color)
    console.log('🎨 FenceContext: selectColor - typ color:', typeof color)
    console.log('🎨 FenceContext: selectColor - color === null?', color === null)
    console.log('🎨 FenceContext: selectColor - color === undefined?', color === undefined)
    dispatch({ type: 'SELECT_COLOR', payload: color })
    console.log('🎨 FenceContext: selectColor - dispatch wysłany')
    // 💰 Przelicz ceny wszystkich elementów po zmianie koloru
    recalculateAllPrices()
  }

  const selectVariant = (variant: string | null) => {
    console.log('🎯 FenceContext: selectVariant wywołane z variant:', variant)
    dispatch({ type: 'SELECT_VARIANT', payload: variant })
    // 💰 Przelicz ceny wszystkich elementów po zmianie wariantu
    recalculateAllPrices(variant || undefined)
  }

  const value: FenceContextType = {
    state,
    dispatch,
    addPost,
    addGate,
    addDoor,
    addSection,
    removeElement,
    updateElement,
    updateSectionWidth, // 🎯 Dodaj funkcję aktualizacji szerokości
    updateGateWidth, // 🚪 Dodaj funkcję aktualizacji szerokości bramy
    updateDoorWidth, // 🚪 Dodaj funkcję aktualizacji szerokości drzwi
    rotateElement,
    resetProject,
    closePerimeter,
    updateTotals,
    selectColor,
    selectVariant,
    recalculateAllPrices,
    onReset
  }

  return (
    <FenceContext.Provider value={value}>
      {children}
    </FenceContext.Provider>
  )
}

export function useFence() {
  const context = useContext(FenceContext)
  if (context === undefined) {
    throw new Error('useFence must be used within a FenceProvider')
  }
  return context
}
